import React from "react";
import { useTranslation } from "react-i18next";
import { Dropdown, Stack, TextField } from "braid-design-system";

interface EmojiOption {
    emoji: string;
    label: string;
}

// Curated set aimed at a hardware / tools / general store. Grouped only for readability
// in the dropdown; extend freely — it is just a list. Any emoji not in this list that is
// already saved on a group is still shown (injected as the first option).
const EMOJI_OPTIONS: EmojiOption[] = [
    // Tools
    { emoji: "🔧", label: "Wrench" },
    { emoji: "🔨", label: "Hammer" },
    { emoji: "🛠️", label: "Hammer & wrench" },
    { emoji: "⚒️", label: "Hammer & pick" },
    { emoji: "🪛", label: "Screwdriver" },
    { emoji: "🪚", label: "Saw" },
    { emoji: "🪓", label: "Axe" },
    { emoji: "⛏️", label: "Pick" },
    { emoji: "🗜️", label: "Clamp" },
    { emoji: "🔩", label: "Nut & bolt" },
    { emoji: "🔗", label: "Chain / link" },
    { emoji: "🧲", label: "Magnet" },
    { emoji: "🧰", label: "Toolbox" },
    { emoji: "📏", label: "Ruler" },
    { emoji: "📐", label: "Set square" },
    // Power & machinery
    { emoji: "⚡", label: "Power" },
    { emoji: "🔌", label: "Plug" },
    { emoji: "🔋", label: "Battery" },
    { emoji: "💡", label: "Bulb" },
    { emoji: "⚙️", label: "Gear" },
    { emoji: "🏗️", label: "Construction" },
    { emoji: "🏭", label: "Factory" },
    { emoji: "🚜", label: "Tractor" },
    { emoji: "🛞", label: "Wheel / tyre" },
    { emoji: "🛢️", label: "Oil drum" },
    { emoji: "⛽", label: "Fuel" },
    // Garden & agriculture
    { emoji: "🌱", label: "Seedling" },
    { emoji: "🌿", label: "Herb" },
    { emoji: "🪴", label: "Potted plant" },
    { emoji: "🌾", label: "Sheaf of rice" },
    { emoji: "🌳", label: "Tree" },
    { emoji: "✂️", label: "Scissors / shears" },
    { emoji: "💧", label: "Water drop" },
    { emoji: "🚿", label: "Shower / hose" },
    // Safety & welding
    { emoji: "🔥", label: "Fire / welding" },
    { emoji: "🧯", label: "Fire extinguisher" },
    { emoji: "🥽", label: "Goggles" },
    { emoji: "🧤", label: "Gloves" },
    { emoji: "⛑️", label: "Safety helmet" },
    { emoji: "🦺", label: "Safety vest" },
    { emoji: "⚠️", label: "Warning" },
    // Household
    { emoji: "🏠", label: "House" },
    { emoji: "🪑", label: "Chair" },
    { emoji: "🚪", label: "Door" },
    { emoji: "🪟", label: "Window" },
    { emoji: "🧹", label: "Broom" },
    { emoji: "🪣", label: "Bucket" },
    { emoji: "🧽", label: "Sponge" },
    { emoji: "🔑", label: "Key" },
    // General
    { emoji: "📦", label: "Box" },
    { emoji: "🏷️", label: "Tag" },
    { emoji: "🛒", label: "Trolley" },
    { emoji: "⭐", label: "Star" },
    { emoji: "🧷", label: "Safety pin" },
    { emoji: "🖇️", label: "Clips" },
];

interface EmojiPickerFieldProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    tone?: "critical";
    message?: React.ReactNode;
    disabled?: boolean;
}

const EmojiPickerField: React.FC<EmojiPickerFieldProps> = ({
    label,
    value,
    onChange,
    tone,
    message,
    disabled = false,
}) => {
    const { t } = useTranslation();

    const trimmed = value.trim();
    const isKnown = EMOJI_OPTIONS.some((option) => option.emoji === trimmed);
    // When the dropdown value is a custom emoji not in the list, keep it visible.
    const showCustomOption = trimmed !== "" && !isKnown;

    return (
        <Stack space="small">
            <Dropdown
                label={label}
                value={trimmed}
                tone={tone}
                message={message}
                disabled={disabled}
                onChange={(e) => onChange((e.target as HTMLSelectElement).value)}
            >
                {[
                    <option key="__none__" value="">
                        {t("page.item_group_management.drawer.emoji.none", { defaultValue: "No icon" })}
                    </option>,
                    ...(showCustomOption
                        ? [
                              <option key="__custom__" value={trimmed}>
                                  {trimmed} — {t("page.item_group_management.drawer.emoji.custom", { defaultValue: "custom" })}
                              </option>,
                          ]
                        : []),
                    ...EMOJI_OPTIONS.map((option) => (
                        <option key={option.emoji} value={option.emoji}>
                            {option.emoji}  {option.label}
                        </option>
                    )),
                ]}
            </Dropdown>
            <TextField
                label={t("page.item_group_management.drawer.emoji.custom_label", { defaultValue: "Or paste a custom emoji" })}
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(e.currentTarget.value)}
                placeholder="🏷️"
            />
        </Stack>
    );
};

export default EmojiPickerField;
